/**
 * =============================================================================
 * SPEQ55 RFP Pro - Parameter Set Create / Edit Form
 * =============================================================================
 *
 * WHAT THIS FILE DOES
 * -------------------
 * A single React component that serves TWO routes:
 *
 *     /app/parameter-sets/new         → create mode  (id undefined)
 *     /app/parameter-sets/:id/edit    → edit mode    (id present)
 *
 * It lets the user name a parameter set, write an optional description,
 * choose a visibility scope (create mode only), and curate the keyword /
 * technical-spec arrays using the SAME KeywordSelector and SpecSelector
 * components that RFPAnalyze uses. Author experience == runtime experience.
 *
 * WHY IT EXISTS
 * -------------
 * Power users need a place to assemble reusable keyword / spec collections
 * once and then pull them in during analyses. Before this page existed they
 * had to rebuild the same list on every RFP. We deliberately keep the form
 * minimal: name, description, visibility, two selectors, save — because the
 * primary workflow is "I just want to tweak my favourite list and move on,"
 * not "I'm designing a complex taxonomy."
 *
 * HOW IT WORKS
 * ------------
 * 1. On mount: if :id is present (edit mode), GET /api/parameter-sets/:id
 *    and hydrate the form state from the response. Create mode skips the
 *    fetch and starts with sensible defaults.
 * 2. Submit: on create → POST /api/parameter-sets; on edit → PUT the same
 *    id. Either way, navigate back to /app/parameter-sets on success.
 * 3. Visibility radios are shown ONLY in create mode. Editing a set's
 *    visibility after the fact goes through the list page's dedicated
 *    Share / Unshare / Promote buttons, which map to distinct backend
 *    endpoints because each transition has different authorization rules.
 *
 * VISIBILITY RULES (enforced by both frontend + backend)
 * ------------------------------------------------------
 *   - end_user may create `private` or `shared`
 *   - org_admin (and above) may additionally create `org_default`
 *   - Creator may toggle private <-> shared via the list page buttons
 *   - Only org_admin may promote shared → org_default
 *
 * DEPENDENCIES
 * ------------
 *   react-router-dom              - useParams, useNavigate, Link
 *   ../../components/analysis/KeywordSelector
 *                                 - shared keyword chip editor (reused as-is)
 *   ../../components/analysis/SpecSelector
 *                                 - shared spec chip editor (reused as-is)
 *   ../../api/parameterSetsApi    - getParameterSet / createParameterSet /
 *                                   updateParameterSet thin axios wrappers
 *   ../../contexts/AuthContext    - hasMinRole for gating the org_default
 *                                   radio option
 *
 * DEPENDED ON BY
 * --------------
 *   frontend/src/App.jsx          - route definitions for create/edit
 *   frontend/src/pages/parameter-sets/ParameterSetsList.jsx
 *                                 - navigates here via Edit button
 *
 * @module pages/parameter-sets/ParameterSetForm
 * @author SPEQ55
 */

import { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import KeywordSelector from '../../components/analysis/KeywordSelector';
import SpecSelector from '../../components/analysis/SpecSelector';
import {
  getParameterSet,
  createParameterSet,
  updateParameterSet,
} from '../../api/parameterSetsApi';
import { useAuth } from '../../contexts/AuthContext';

// Radio option metadata for the create-mode visibility picker.
// The `value` strings are wire-compatible with the backend enum. Changing
// them here will break POST /api/parameter-sets validation — keep in sync
// with the CHECK constraint in the parameter_sets table.
const VISIBILITY_OPTIONS = [
  {
    value: 'private',
    label: 'Private',
    description: 'Only visible to you.',
  },
  {
    value: 'shared',
    label: 'Shared in organization',
    description: 'Visible to every member of your organization.',
  },
  {
    value: 'org_default',
    label: 'Organization default',
    description: 'Pre-selected for everyone in the organization (org admin only).',
  },
];

function ParameterSetForm() {
  // React Router params: `id` is present only on /:id/edit routes.
  const { id } = useParams();
  const navigate = useNavigate();
  const { hasMinRole } = useAuth();

  // `isEdit` is the single source of truth that distinguishes the two route
  // modes throughout the component — drives whether we fetch on mount,
  // whether the visibility radios render, and which API call Submit makes.
  const isEdit = Boolean(id);

  // ---------------------------------------------------------------------------
  // State
  // ---------------------------------------------------------------------------
  //   loading        - only true on initial fetch in edit mode (create mode
  //                    skips straight to the form)
  //   saving         - blocks duplicate submits and disables both buttons
  //   error          - top-level error banner (validation or server error)
  //   name/description/visibility/keywords/technicalSpecs
  //                  - the editable form values
  // ---------------------------------------------------------------------------
  const [loading, setLoading] = useState(isEdit);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [visibility, setVisibility] = useState('private');
  const [keywords, setKeywords] = useState([]);
  const [technicalSpecs, setTechnicalSpecs] = useState([]);

  // org_admin or higher unlocks the org_default radio option. Non-admins see
  // it greyed out with a tooltip-ish description so they understand WHY it's
  // disabled rather than seeing it mysteriously absent.
  const canCreateOrgDefault = hasMinRole('org_admin');

  /**
   * Fetches the parameter set when in edit mode and hydrates the form.
   *
   * Wrapped in useCallback so the useEffect below depends on a stable
   * reference — without this, React would re-run the effect on every render.
   *
   * The `technicalSpecs || technical_specs` fallback covers both the
   * camelCased REST response shape and the snake_cased raw row shape that
   * some code paths return during the tenancy migration rollout. Remove the
   * snake_case fallback once every model consistently emits camelCase.
   */
  const loadSet = useCallback(async () => {
    if (!isEdit) return;
    setLoading(true);
    setError(null);
    try {
      const set = await getParameterSet(id);
      setName(set.name || '');
      setDescription(set.description || '');
      setVisibility(set.visibility || 'private');
      setKeywords(set.keywords || []);
      setTechnicalSpecs(set.technicalSpecs || set.technical_specs || []);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to load parameter set.');
    } finally {
      setLoading(false);
    }
  }, [id, isEdit]);

  // Runs once after mount in edit mode, never in create mode.
  useEffect(() => {
    loadSet();
  }, [loadSet]);

  /**
   * Form submit handler — branches on isEdit to hit the right endpoint.
   *
   * Client-side validation is minimal (just a non-blank name) because the
   * backend ParameterSet.create()/update() already:
   *   - trims name and description
   *   - de-duplicates and sanitizes keyword/spec arrays
   *   - validates visibility against the enum
   * Duplicating that logic here would risk drift. We only catch the
   * obvious UX-killing case (empty name) to give immediate feedback.
   *
   * On success we navigate back to the list; on failure we STAY on the
   * form with an error banner and unlock the Save button so the user can
   * fix and retry without losing their edits.
   */
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!name.trim()) {
      setError('Name is required.');
      return;
    }

    setSaving(true);
    setError(null);
    try {
      if (isEdit) {
        // Note: visibility is intentionally omitted from the update payload.
        // Changing visibility uses dedicated share/unshare/promote endpoints
        // with their own authorization rules, reached via the list page.
        await updateParameterSet(id, {
          name: name.trim(),
          description: description.trim() || null,
          keywords,
          technicalSpecs,
        });
      } else {
        await createParameterSet({
          name: name.trim(),
          description: description.trim() || null,
          visibility,
          keywords,
          technicalSpecs,
        });
      }
      navigate('/app/parameter-sets');
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to save parameter set.');
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-96">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-primary-200 border-t-primary-600 rounded-full animate-spin mx-auto" />
          <p className="mt-4 text-accent-500">Loading parameter set...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6 max-w-4xl">
      {/* Header */}
      <div>
        <Link to="/app/parameter-sets" className="text-sm text-primary-600 hover:text-primary-700">
          ← Back to parameter sets
        </Link>
        <h1 className="text-2xl font-bold text-accent-800 mt-2">
          {isEdit ? 'Edit Parameter Set' : 'New Parameter Set'}
        </h1>
        <p className="mt-1 text-accent-500">
          Keywords and technical specs defined here can be selected when starting an RFP analysis.
        </p>
      </div>

      {error && (
        <div className="bg-error-light border-l-4 border-error p-4 rounded">
          <p className="text-error-dark">{error}</p>
        </div>
      )}

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Basic info */}
        <div className="card">
          <div className="card-header">
            <h2 className="text-lg font-semibold text-accent-800">Details</h2>
          </div>
          <div className="card-body space-y-4">
            <div>
              <label htmlFor="ps-name" className="block text-sm font-medium text-accent-700 mb-1">
                Name <span className="text-error">*</span>
              </label>
              <input
                id="ps-name"
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                required
                maxLength={255}
                className="input w-full"
                placeholder="e.g. Mechanical RFPs - EU Region"
              />
            </div>

            <div>
              <label
                htmlFor="ps-description"
                className="block text-sm font-medium text-accent-700 mb-1"
              >
                Description
              </label>
              <textarea
                id="ps-description"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                rows={3}
                className="input w-full"
                placeholder="Optional notes about when to use this set."
              />
            </div>

            {!isEdit && (
              <div>
                <span className="block text-sm font-medium text-accent-700 mb-2">Visibility</span>
                <div className="space-y-2">
                  {VISIBILITY_OPTIONS.map((opt) => {
                    const disabled = opt.value === 'org_default' && !canCreateOrgDefault;
                    return (
                      <label
                        key={opt.value}
                        className={`flex items-start gap-3 p-3 border rounded-lg cursor-pointer transition-colors ${
                          visibility === opt.value
                            ? 'border-primary-500 bg-primary-50'
                            : 'border-accent-200 hover:bg-accent-50'
                        } ${disabled ? 'opacity-50 cursor-not-allowed' : ''}`}
                      >
                        <input
                          type="radio"
                          name="visibility"
                          value={opt.value}
                          checked={visibility === opt.value}
                          onChange={() => setVisibility(opt.value)}
                          disabled={disabled}
                          className="mt-1"
                        />
                        <div>
                          <p className="text-sm font-medium text-accent-800">{opt.label}</p>
                          <p className="text-xs text-accent-500">{opt.description}</p>
                        </div>
                      </label>
                    );
                  })}
                </div>
              </div>
            )}

            {isEdit && (
              <p className="text-xs text-accent-500">
                Use the list page to share, unshare, or promote this set.
              </p>
            )}
          </div>
        </div>

        {/* Keywords */}
        <div className="card">
          <div className="card-header">
            <h2 className="text-lg font-semibold text-accent-800">Keywords</h2>
            <p className="text-sm text-accent-500 mt-1">
              Terms the AI will search for in the RFP document.
            </p>
          </div>
          <div className="card-body">
            <KeywordSelector
              keywords={keywords}
              onChange={setKeywords}
              availableKeywords={keywords}
            />
          </div>
        </div>

        {/* Technical specs */}
        <div className="card">
          <div className="card-header">
            <h2 className="text-lg font-semibold text-accent-800">Technical Specifications</h2>
            <p className="text-sm text-accent-500 mt-1">
              Parameters the AI will attempt to extract from the document.
            </p>
          </div>
          <div className="card-body">
            <SpecSelector
              specs={technicalSpecs}
              onChange={setTechnicalSpecs}
              availableSpecs={technicalSpecs}
            />
          </div>
        </div>

        {/* Actions */}
        <div className="flex justify-end gap-3">
          <button
            type="button"
            onClick={() => navigate('/app/parameter-sets')}
            disabled={saving}
            className="btn-outline"
          >
            Cancel
          </button>
          <button type="submit" disabled={saving} className="btn-primary">
            {saving ? 'Saving...' : isEdit ? 'Save Changes' : 'Create Parameter Set'}
          </button>
        </div>
      </form>
    </div>
  );
}

export default ParameterSetForm;
